
/**
 * Permission constants for RBAC (Role-Based Access Control)
 *
 * Permission naming convention:
 * - Format: {resource}:{action}
 * - Resources: tenant, user, order, strategy, symbol, etc.
 * - Actions: read, create, update, delete, manage (all), suspend, etc.
 *
 * Roles are hierarchical:
 * - system-admin: Full system access (all tenants)
 * - tenant-admin: Full tenant access (their tenant only)
 * - tenant-operator: Operational access (limited admin)
 * - tenant-viewer: Read-only access
 */

// ============================================================================
// TENANT PERMISSIONS
// ============================================================================

export const TENANT_PERMISSIONS = {
  /** View tenant list and details */
  READ: 'tenant:read',
  /** Create new tenants */
  CREATE: 'tenant:create',
  /** Update tenant details */
  UPDATE: 'tenant:update',
  /** Delete tenants */
  DELETE: 'tenant:delete',
  /** Full tenant management (includes all above) */
  MANAGE: 'tenant:manage',
  /** Suspend/reactivate tenants */
  SUSPEND: 'tenant:suspend',
  /** View tenant health and metrics */
  HEALTH: 'tenant:health',
  /** Manage tenant quotas and limits */
  QUOTA: 'tenant:quota',
  /** Manage tenant billing and subscriptions */
  BILLING: 'tenant:billing',
  /** Manage tenant isolation settings */
  ISOLATION: 'tenant:isolation',
  /** Manage tenant configuration */
  CONFIG: 'tenant:config',
  /** Manage tenant branding and customization */
  BRANDING: 'tenant:branding',
} as const;

/** Type for tenant permissions */
export type TenantPermission = typeof TENANT_PERMISSIONS[keyof typeof TENANT_PERMISSIONS];

// ============================================================================
// USER PERMISSIONS
// ============================================================================

export const USER_PERMISSIONS = {
  /** View users */
  READ: 'user:read',
  /** Create users */
  CREATE: 'user:create',
  /** Update users */
  UPDATE: 'user:update',
  /** Delete users */
  DELETE: 'user:delete',
  /** Full user management */
  MANAGE: 'user:manage',
  /** Assign roles to users */
  ASSIGN_ROLE: 'user:assign_role',
  /** Reset user passwords */
  RESET_PASSWORD: 'user:reset_password',
  /** Suspend/activate users */
  SUSPEND: 'user:suspend',
} as const;

/** Type for user permissions */
export type UserPermission = typeof USER_PERMISSIONS[keyof typeof USER_PERMISSIONS];

// ============================================================================
// ROLE PERMISSIONS
// ============================================================================

export const ROLE_PERMISSIONS = {
  /** View roles */
  READ: 'role:read',
  /** Create roles */
  CREATE: 'role:create',
  /** Update roles */
  UPDATE: 'role:update',
  /** Delete roles */
  DELETE: 'role:delete',
  /** Full role management */
  MANAGE: 'role:manage',
  /** Assign permissions to roles */
  ASSIGN_PERMISSION: 'role:assign_permission',
} as const;

/** Type for role permissions */
export type RolePermission = typeof ROLE_PERMISSIONS[keyof typeof ROLE_PERMISSIONS];

// ============================================================================
// TRADING PERMISSIONS
// ============================================================================

export const TRADING_PERMISSIONS = {
  /** View orders */
  READ_ORDERS: 'trading:read_orders',
  /** Create orders */
  CREATE_ORDER: 'trading:create_order',
  /** Cancel orders */
  CANCEL_ORDER: 'trading:cancel_order',
  /** View positions */
  READ_POSITIONS: 'trading:read_positions',
  /** Full trading access */
  TRADE: 'trading:trade',
  /** View trading history */
  READ_HISTORY: 'trading:read_history',
} as const;

/** Type for trading permissions */
export type TradingPermission = typeof TRADING_PERMISSIONS[keyof typeof TRADING_PERMISSIONS];

// ============================================================================
// STRATEGY PERMISSIONS
// ============================================================================

export const STRATEGY_PERMISSIONS = {
  /** View strategies */
  READ: 'strategy:read',
  /** Create strategies */
  CREATE: 'strategy:create',
  /** Update strategies */
  UPDATE: 'strategy:update',
  /** Delete strategies */
  DELETE: 'strategy:delete',
  /** Start/stop strategies */
  CONTROL: 'strategy:control',
  /** Full strategy management */
  MANAGE: 'strategy:manage',
  /** Run backtests */
  BACKTEST: 'strategy:backtest',
} as const;

/** Type for strategy permissions */
export type StrategyPermission = typeof STRATEGY_PERMISSIONS[keyof typeof STRATEGY_PERMISSIONS];

// ============================================================================
// SYMBOL PERMISSIONS
// ============================================================================

export const SYMBOL_PERMISSIONS = {
  /** View symbols */
  READ: 'symbol:read',
  /** Create symbols */
  CREATE: 'symbol:create',
  /** Update symbols */
  UPDATE: 'symbol:update',
  /** Delete symbols */
  DELETE: 'symbol:delete',
  /** Full symbol management */
  MANAGE: 'symbol:manage',
  /** Map symbols across exchanges */
  MAP: 'symbol:map',
} as const;

/** Type for symbol permissions */
export type SymbolPermission = typeof SYMBOL_PERMISSIONS[keyof typeof SYMBOL_PERMISSIONS];

// ============================================================================
// HFT PERMISSIONS
// ============================================================================

export const HFT_PERMISSIONS = {
  /** View HFT metrics and dashboards */
  READ: 'hft:read',
  /** Export HFT metrics data */
  EXPORT: 'hft:export',
  /** Configure HFT settings (alerts, thresholds) */
  CONFIGURE: 'hft:configure',
  /** Full HFT management */
  MANAGE: 'hft:manage',
} as const;

/** Type for HFT permissions */
export type HftPermission = typeof HFT_PERMISSIONS[keyof typeof HFT_PERMISSIONS];

// ============================================================================
// EXCHANGE PERMISSIONS (DEV-222, DEV-223, DEV-224, DEV-225)
// ============================================================================

export const EXCHANGE_PERMISSIONS = {
  /** View exchange connections */
  READ: 'exchange:read',
  /** Create exchange connections */
  CREATE: 'exchange:create',
  /** Update exchange connections */
  UPDATE: 'exchange:update',
  /** Delete exchange connections */
  DELETE: 'exchange:delete',
  /** Full exchange management (includes credentials admin) */
  ADMIN: 'exchange:admin',
  /** Test exchange connections */
  TEST: 'exchange:test',
  /** Rotate exchange credentials */
  ROTATE: 'exchange:rotate',
  /** View supported markets */
  MARKETS: 'exchange:markets',
  /** Trigger manual sync */
  SYNC: 'exchange:sync',
} as const;

/** Type for exchange permissions */
export type ExchangePermission = typeof EXCHANGE_PERMISSIONS[keyof typeof EXCHANGE_PERMISSIONS];

// ============================================================================
// SYSTEM PERMISSIONS
// ============================================================================

export const SYSTEM_PERMISSIONS = {
  /** View system health and metrics */
  HEALTH: 'system:health',
  /** View audit logs */
  AUDIT: 'system:audit',
  /** Manage system configuration */
  CONFIG: 'system:config',
  /** View system settings */
  READ_SETTINGS: 'system:read_settings',
  /** Update system settings */
  UPDATE_SETTINGS: 'system:update_settings',
  /** Full system administration */
  ADMIN: 'system:admin',
} as const;

/** Type for system permissions */
export type SystemPermission = typeof SYSTEM_PERMISSIONS[keyof typeof SYSTEM_PERMISSIONS];

// ============================================================================
// SCHEMA PERMISSIONS
// ============================================================================

export const SCHEMA_PERMISSIONS = {
  /** View schemas */
  READ: 'schema:read',
  /** Create schemas */
  CREATE: 'schema:create',
  /** Update schemas */
  UPDATE: 'schema:update',
  /** Delete schemas */
  DELETE: 'schema:delete',
  /** Compare schemas between environments */
  COMPARE: 'schema:compare',
  /** Full schema management */
  MANAGE: 'schema:manage',
} as const;

/** Type for schema permissions */
export type SchemaPermission = typeof SCHEMA_PERMISSIONS[keyof typeof SCHEMA_PERMISSIONS];

// ============================================================================
// DICTIONARY PERMISSIONS
// ============================================================================

export const DICTIONARY_PERMISSIONS = {
  /** View data dictionary */
  READ: 'dictionary:read',
  /** Update data dictionary */
  UPDATE: 'dictionary:update',
  /** Full dictionary management */
  MANAGE: 'dictionary:manage',
} as const;

/** Type for dictionary permissions */
export type DictionaryPermission = typeof DICTIONARY_PERMISSIONS[keyof typeof DICTIONARY_PERMISSIONS];

// ============================================================================
// MIGRATION PERMISSIONS
// ============================================================================

export const MIGRATION_PERMISSIONS = {
  /** View migrations */
  READ: 'migration:read',
  /** Create migrations */
  CREATE: 'migration:create',
  /** Execute migrations */
  EXECUTE: 'migration:execute',
  /** Rollback migrations */
  ROLLBACK: 'migration:rollback',
  /** Run dry-run migrations */
  DRYRUN: 'migration:dryrun',
  /** Full migration management */
  MANAGE: 'migration:manage',
} as const;

/** Type for migration permissions */
export type MigrationPermission = typeof MIGRATION_PERMISSIONS[keyof typeof MIGRATION_PERMISSIONS];

// ============================================================================
// BACKUP PERMISSIONS
// ============================================================================

export const BACKUP_PERMISSIONS = {
  /** Create schema backups */
  CREATE: 'backup:create',
  /** Restore schema from backup */
  RESTORE: 'backup:restore',
  /** View backup history and details */
  READ: 'backup:read',
  /** Delete schema backups */
  DELETE: 'backup:delete',
} as const;

/** Type for backup permissions */
export type BackupPermission = typeof BACKUP_PERMISSIONS[keyof typeof BACKUP_PERMISSIONS];

// ============================================================================
// ML ANOMALY DETECTION PERMISSIONS
// ============================================================================

export const ANOMALY_PERMISSIONS = {
  /** View anomaly detection dashboards and results */
  VIEW: 'anomaly:view',
  /** Train and manage ML models */
  TRAIN: 'anomaly:train',
  /** Configure automated actions and detection settings */
  CONFIGURE: 'anomaly:configure',
} as const;

/** Type for anomaly detection permissions */
export type AnomalyPermission = typeof ANOMALY_PERMISSIONS[keyof typeof ANOMALY_PERMISSIONS];

// ============================================================================
// CONFIGURATION PERMISSIONS
// ============================================================================

// CANONICAL FORM: `config:<action>` (colon), matching every other resource and
// the backend RBAC catalog (crates/razor_security/src/rbac/permissions.rs) +
// authorization middleware, which check colon-form `config:read`/`config:write`/etc.
// These were previously dot-form (`config.read`) — the lone resource using a dot —
// which made config-management's colon-form route guards (`config:read`) never match
// the role-derived grants, locking every operator (super_admin included) out of the
// section with a redirect to /unauthorized. A companion migration renames the prod
// `permissions.name` rows `config.* -> config:*` so explicit grants + the
// effective-permissions endpoint are canonical too.
// The backend RBAC catalog defines exactly SIX config permissions —
// config:read, config:write, config:rollback, config:apply, config:admin,
// config:read_sensitive. The finer-grained UI capabilities below therefore map
// (collapse) onto that canonical set rather than onto dedicated permissions that
// were never seeded. Route access is additionally gated by TENANT_ADMIN/
// SYSTEM_ADMIN roles (config-admin.guard.ts) and the finer distinctions
// (delete/approve/production/staging) are enforced there and by the backend
// endpoints. NOTE: the no-role-bypass guard checks (configAdminGuard's
// requiredPermissions — READ on the dashboard, AUDIT on history, WRITE on the
// editor) MUST resolve to a permission operators actually hold, which is why
// AUDIT collapses to config:read (viewing history is a read).
export const CONFIG_PERMISSIONS = {
  /** Read configuration entries */
  READ: 'config:read',
  /** Create/update configuration entries */
  WRITE: 'config:write',
  /** Delete configuration entries (destructive → admin-tier) */
  DELETE: 'config:admin',
  /** View configuration change history (a read of change history) */
  AUDIT: 'config:read',
  /** Approve configuration changes (admin-tier) */
  APPROVE: 'config:admin',
  /** Perform bulk operations (import, export, sync → write-class) */
  BULK: 'config:write',
  /** Access production environment configuration (elevated → admin-tier) */
  PRODUCTION: 'config:admin',
  /** Access sensitive/encrypted configuration values */
  SENSITIVE: 'config:read_sensitive',
  /** System-wide configuration administration */
  ADMIN: 'config:admin',
  /** Access staging environment configuration (a read of staging config) */
  STAGING: 'config:read',
} as const;

/** Type for configuration permissions */
export type ConfigPermission = typeof CONFIG_PERMISSIONS[keyof typeof CONFIG_PERMISSIONS];

// ============================================================================
// ROLES
// ============================================================================

export const ROLES = {
  /** System administrator - full access to all tenants */
  SYSTEM_ADMIN: 'system-admin',
  /** Tenant administrator - full access to their tenant */
  TENANT_ADMIN: 'tenant-admin',
  /** Tenant operator - operational access */
  TENANT_OPERATOR: 'tenant-operator',
  /** Tenant viewer - read-only access */
  TENANT_VIEWER: 'tenant-viewer',
  /** Trader - trading operations */
  TRADER: 'trader',
  /** Strategy manager - strategy operations */
  STRATEGY_MANAGER: 'strategy-manager',
  /** Schema editor - schema management operations */
  SCHEMA_EDITOR: 'schema-editor',
  /** Symbol admin - symbol mapping administration across exchanges */
  SYMBOL_ADMIN: 'symbol-admin',
  /** Database administrator - full database/migration access */
  DBA: 'dba',
  /** Read-only user */
  READONLY: 'readonly',
} as const;

/** Type for roles */
export type Role = typeof ROLES[keyof typeof ROLES];

/**
 * Canonical role catalog: human-readable label + description for every role
 * defined in {@link ROLES}. This is the single source of truth for any UI that
 * presents roles for selection/assignment, ensuring the value posted to the
 * backend is always a canonical kebab-case role string (e.g. 'tenant-admin').
 *
 * Keyed by {@link Role} so adding a role to ROLES forces a catalog entry
 * (compile-time exhaustiveness via Record<Role, ...>).
 */
export const ROLE_CATALOG: Record<Role, { label: string; description: string }> = {
  [ROLES.SYSTEM_ADMIN]: {
    label: 'System Admin',
    description: 'Full system access across all tenants',
  },
  [ROLES.TENANT_ADMIN]: {
    label: 'Tenant Admin',
    description: 'Full administrative access within the tenant',
  },
  [ROLES.TENANT_OPERATOR]: {
    label: 'Tenant Operator',
    description: 'Operational access with limited administration',
  },
  [ROLES.TENANT_VIEWER]: {
    label: 'Tenant Viewer',
    description: 'Read-only access within the tenant',
  },
  [ROLES.TRADER]: {
    label: 'Trader',
    description: 'Create, cancel, and view trading orders and positions',
  },
  [ROLES.STRATEGY_MANAGER]: {
    label: 'Strategy Manager',
    description: 'Manage strategies and run backtests',
  },
  [ROLES.SCHEMA_EDITOR]: {
    label: 'Schema Editor',
    description: 'Manage schemas, data dictionary, and migrations',
  },
  [ROLES.SYMBOL_ADMIN]: {
    label: 'Symbol Admin',
    description: 'Administer symbol mappings across exchanges',
  },
  [ROLES.DBA]: {
    label: 'Database Administrator',
    description: 'Full database, migration, and backup access',
  },
  [ROLES.READONLY]: {
    label: 'Read-Only',
    description: 'Read-only access to operational data',
  },
};

/** A single assignable-role option (canonical value + display metadata). */
export interface RoleOption {
  /** Canonical kebab-case role string sent to the backend. */
  value: Role;
  label: string;
  description: string;
}

/**
 * Flattened, ordered list of all canonical roles for selection UIs (e.g. the
 * user-detail role-assignment dialog). The `value` is the canonical role string
 * the backend recognizes; never present non-canonical aliases for assignment.
 */
export const ASSIGNABLE_ROLES: readonly RoleOption[] = Object.values(ROLES).map((value) => ({
  value,
  label: ROLE_CATALOG[value].label,
  description: ROLE_CATALOG[value].description,
}));

// ============================================================================
// PERMISSION GROUPS (for route guards)
// ============================================================================

/**
 * Permission groups for common route protection scenarios
 * These can be used directly in route.data['requiredPermissions']
 */
export const PERMISSION_GROUPS = {
  /** Permissions for tenant list view */
  TENANT_LIST: [TENANT_PERMISSIONS.READ, TENANT_PERMISSIONS.MANAGE],

  /** Permissions for tenant configuration */
  TENANT_CONFIG: [TENANT_PERMISSIONS.CONFIG, TENANT_PERMISSIONS.MANAGE],

  /** Permissions for tenant quotas */
  TENANT_QUOTAS: [TENANT_PERMISSIONS.QUOTA, TENANT_PERMISSIONS.MANAGE],

  /** Permissions for tenant isolation settings */
  TENANT_ISOLATION: [TENANT_PERMISSIONS.ISOLATION, TENANT_PERMISSIONS.MANAGE],

  /** Permissions for tenant billing */
  TENANT_BILLING: [TENANT_PERMISSIONS.BILLING, TENANT_PERMISSIONS.MANAGE],

  /** Permissions for tenant health monitoring */
  TENANT_HEALTH: [TENANT_PERMISSIONS.HEALTH, TENANT_PERMISSIONS.READ, TENANT_PERMISSIONS.MANAGE],

  /** Permissions for tenant branding customization */
  TENANT_BRANDING: [TENANT_PERMISSIONS.BRANDING, TENANT_PERMISSIONS.CONFIG, TENANT_PERMISSIONS.MANAGE],

  /** Permissions for user management */
  USER_MANAGEMENT: [USER_PERMISSIONS.READ, USER_PERMISSIONS.MANAGE],

  /** Permissions for role management */
  ROLE_MANAGEMENT: [ROLE_PERMISSIONS.READ, ROLE_PERMISSIONS.MANAGE],

  /** Permissions for symbol management */
  SYMBOL_MANAGEMENT: [SYMBOL_PERMISSIONS.READ, SYMBOL_PERMISSIONS.MANAGE],

  /** Permissions for trading dashboard */
  TRADING_DASHBOARD: [TRADING_PERMISSIONS.READ_ORDERS, TRADING_PERMISSIONS.READ_POSITIONS],

  /** Permissions for strategy management */
  STRATEGY_MANAGEMENT: [STRATEGY_PERMISSIONS.READ, STRATEGY_PERMISSIONS.MANAGE],

  /** Permissions for system administration */
  SYSTEM_ADMIN: [SYSTEM_PERMISSIONS.ADMIN],

  /** Permissions for HFT metrics view */
  HFT_METRICS: [HFT_PERMISSIONS.READ, HFT_PERMISSIONS.MANAGE],

  /** Permissions for HFT metrics export */
  HFT_EXPORT: [HFT_PERMISSIONS.EXPORT, HFT_PERMISSIONS.MANAGE],

  /** Permissions for HFT configuration */
  HFT_CONFIG: [HFT_PERMISSIONS.CONFIGURE, HFT_PERMISSIONS.MANAGE],

  /** Permissions for exchange management (DEV-222 - DEV-225) */
  EXCHANGE_MANAGEMENT: [EXCHANGE_PERMISSIONS.READ, EXCHANGE_PERMISSIONS.ADMIN],

  /** Permissions for exchange credentials admin */
  EXCHANGE_CREDENTIALS: [EXCHANGE_PERMISSIONS.ADMIN],

  /** Permissions for exchange connection testing */
  EXCHANGE_TEST: [EXCHANGE_PERMISSIONS.TEST, EXCHANGE_PERMISSIONS.ADMIN],

  /** Permissions for exchange markets discovery */
  EXCHANGE_MARKETS: [EXCHANGE_PERMISSIONS.MARKETS, EXCHANGE_PERMISSIONS.READ, EXCHANGE_PERMISSIONS.ADMIN],

  /** Permissions for exchange sync operations */
  EXCHANGE_SYNC: [EXCHANGE_PERMISSIONS.SYNC, EXCHANGE_PERMISSIONS.ADMIN],

  /** Permissions for schema management */
  SCHEMA_MANAGEMENT: [SCHEMA_PERMISSIONS.READ, SCHEMA_PERMISSIONS.MANAGE],

  /** Permissions for data dictionary */
  DICTIONARY_MANAGEMENT: [DICTIONARY_PERMISSIONS.READ, DICTIONARY_PERMISSIONS.MANAGE],

  /** Permissions for database migrations */
  MIGRATION_MANAGEMENT: [MIGRATION_PERMISSIONS.READ, MIGRATION_PERMISSIONS.MANAGE],

  /** Permissions for schema backup and restore */
  BACKUP_MANAGEMENT: [BACKUP_PERMISSIONS.CREATE, BACKUP_PERMISSIONS.RESTORE, BACKUP_PERMISSIONS.READ],

  /** Permissions for ML anomaly detection dashboard */
  ANOMALY_VIEW: [ANOMALY_PERMISSIONS.VIEW],

  /** Permissions for ML model training */
  ANOMALY_TRAIN: [ANOMALY_PERMISSIONS.TRAIN],

  /** Permissions for anomaly detection configuration */
  ANOMALY_CONFIGURE: [ANOMALY_PERMISSIONS.CONFIGURE],

  /** Permissions for environment configuration management */
  CONFIG_MANAGEMENT: [CONFIG_PERMISSIONS.READ, CONFIG_PERMISSIONS.WRITE, CONFIG_PERMISSIONS.ADMIN],

  /** Permissions for environment configuration audit */
  CONFIG_AUDIT: [CONFIG_PERMISSIONS.AUDIT, CONFIG_PERMISSIONS.READ],
} as const;

// ============================================================================
// ROLE PERMISSION MAPPINGS
// ============================================================================

/**
 * Default permission sets for each role
 * Used for reference and initial role setup
 */
export const ROLE_DEFAULT_PERMISSIONS: Record<Role, string[]> = {
  [ROLES.SYSTEM_ADMIN]: [
    // System admins have all permissions
    ...Object.values(TENANT_PERMISSIONS),
    ...Object.values(USER_PERMISSIONS),
    ...Object.values(ROLE_PERMISSIONS),
    ...Object.values(TRADING_PERMISSIONS),
    ...Object.values(STRATEGY_PERMISSIONS),
    ...Object.values(SYMBOL_PERMISSIONS),
    ...Object.values(HFT_PERMISSIONS),
    ...Object.values(EXCHANGE_PERMISSIONS),
    ...Object.values(SYSTEM_PERMISSIONS),
    ...Object.values(SCHEMA_PERMISSIONS),
    ...Object.values(DICTIONARY_PERMISSIONS),
    ...Object.values(MIGRATION_PERMISSIONS),
    ...Object.values(BACKUP_PERMISSIONS),
    ...Object.values(ANOMALY_PERMISSIONS),
    ...Object.values(CONFIG_PERMISSIONS),
  ],
  [ROLES.TENANT_ADMIN]: [
    // Tenant admins can manage their tenant
    TENANT_PERMISSIONS.READ,
    TENANT_PERMISSIONS.UPDATE,
    TENANT_PERMISSIONS.HEALTH,
    TENANT_PERMISSIONS.QUOTA,
    TENANT_PERMISSIONS.CONFIG,
    TENANT_PERMISSIONS.BRANDING,
    ...Object.values(USER_PERMISSIONS),
    ROLE_PERMISSIONS.READ,
    ROLE_PERMISSIONS.ASSIGN_PERMISSION,
    ...Object.values(TRADING_PERMISSIONS),
    ...Object.values(STRATEGY_PERMISSIONS),
    ...Object.values(SYMBOL_PERMISSIONS),
    HFT_PERMISSIONS.READ,
    HFT_PERMISSIONS.EXPORT,
    HFT_PERMISSIONS.CONFIGURE,
    // Exchange management for tenant admins
    ...Object.values(EXCHANGE_PERMISSIONS),
    // Schema management for tenant admins
    ...Object.values(SCHEMA_PERMISSIONS),
    ...Object.values(DICTIONARY_PERMISSIONS),
    MIGRATION_PERMISSIONS.READ,
    MIGRATION_PERMISSIONS.CREATE,
    MIGRATION_PERMISSIONS.DRYRUN,
    BACKUP_PERMISSIONS.READ,
    SYSTEM_PERMISSIONS.HEALTH,
    SYSTEM_PERMISSIONS.AUDIT,
    ...Object.values(ANOMALY_PERMISSIONS),
    // Config management for tenant admins (not production/sensitive/admin)
    CONFIG_PERMISSIONS.READ,
    CONFIG_PERMISSIONS.WRITE,
    CONFIG_PERMISSIONS.AUDIT,
    CONFIG_PERMISSIONS.APPROVE,
    CONFIG_PERMISSIONS.BULK,
  ],
  [ROLES.TENANT_OPERATOR]: [
    TENANT_PERMISSIONS.READ,
    TENANT_PERMISSIONS.HEALTH,
    USER_PERMISSIONS.READ,
    USER_PERMISSIONS.UPDATE,
    ROLE_PERMISSIONS.READ,
    ...Object.values(TRADING_PERMISSIONS),
    STRATEGY_PERMISSIONS.READ,
    STRATEGY_PERMISSIONS.CONTROL,
    SYMBOL_PERMISSIONS.READ,
    SYMBOL_PERMISSIONS.MAP,
    HFT_PERMISSIONS.READ,
    // Limited exchange access for operators
    EXCHANGE_PERMISSIONS.READ,
    EXCHANGE_PERMISSIONS.TEST,
    EXCHANGE_PERMISSIONS.MARKETS,
    SCHEMA_PERMISSIONS.READ,
    DICTIONARY_PERMISSIONS.READ,
    MIGRATION_PERMISSIONS.READ,
    SYSTEM_PERMISSIONS.HEALTH,
    ANOMALY_PERMISSIONS.VIEW,
  ],
  [ROLES.TENANT_VIEWER]: [
    TENANT_PERMISSIONS.READ,
    TENANT_PERMISSIONS.HEALTH,
    USER_PERMISSIONS.READ,
    ROLE_PERMISSIONS.READ,
    TRADING_PERMISSIONS.READ_ORDERS,
    TRADING_PERMISSIONS.READ_POSITIONS,
    TRADING_PERMISSIONS.READ_HISTORY,
    STRATEGY_PERMISSIONS.READ,
    SYMBOL_PERMISSIONS.READ,
    SCHEMA_PERMISSIONS.READ,
    DICTIONARY_PERMISSIONS.READ,
    SYSTEM_PERMISSIONS.HEALTH,
    ANOMALY_PERMISSIONS.VIEW,
  ],
  [ROLES.TRADER]: [
    TRADING_PERMISSIONS.READ_ORDERS,
    TRADING_PERMISSIONS.CREATE_ORDER,
    TRADING_PERMISSIONS.CANCEL_ORDER,
    TRADING_PERMISSIONS.READ_POSITIONS,
    TRADING_PERMISSIONS.READ_HISTORY,
    SYMBOL_PERMISSIONS.READ,
  ],
  // GAP-SB-002: mirror the backend role catalog exactly — the strategy-manager
  // arm grants strategy authoring (create/read/update) and read-only trading,
  // while delete/control/manage stay behind trading:execute server-side.
  // Spreading all STRATEGY_PERMISSIONS here made the FE render affordances
  // that could only ever 403.
  [ROLES.STRATEGY_MANAGER]: [
    STRATEGY_PERMISSIONS.READ,
    STRATEGY_PERMISSIONS.CREATE,
    STRATEGY_PERMISSIONS.UPDATE,
    STRATEGY_PERMISSIONS.BACKTEST,
    TRADING_PERMISSIONS.READ_ORDERS,
    TRADING_PERMISSIONS.READ_POSITIONS,
    TRADING_PERMISSIONS.READ_HISTORY,
    SYMBOL_PERMISSIONS.READ,
  ],
  [ROLES.SCHEMA_EDITOR]: [
    ...Object.values(SCHEMA_PERMISSIONS),
    ...Object.values(DICTIONARY_PERMISSIONS),
    ...Object.values(MIGRATION_PERMISSIONS),
    SYSTEM_PERMISSIONS.HEALTH,
  ],
  [ROLES.SYMBOL_ADMIN]: [
    ...Object.values(SYMBOL_PERMISSIONS),
    EXCHANGE_PERMISSIONS.READ,
    EXCHANGE_PERMISSIONS.MARKETS,
    SYSTEM_PERMISSIONS.HEALTH,
  ],
  [ROLES.DBA]: [
    ...Object.values(SCHEMA_PERMISSIONS),
    ...Object.values(DICTIONARY_PERMISSIONS),
    ...Object.values(MIGRATION_PERMISSIONS),
    ...Object.values(BACKUP_PERMISSIONS),
    SYSTEM_PERMISSIONS.HEALTH,
    SYSTEM_PERMISSIONS.AUDIT,
  ],
  [ROLES.READONLY]: [
    TENANT_PERMISSIONS.READ,
    USER_PERMISSIONS.READ,
    ROLE_PERMISSIONS.READ,
    TRADING_PERMISSIONS.READ_ORDERS,
    TRADING_PERMISSIONS.READ_POSITIONS,
    TRADING_PERMISSIONS.READ_HISTORY,
    STRATEGY_PERMISSIONS.READ,
    SYMBOL_PERMISSIONS.READ,
    SYSTEM_PERMISSIONS.HEALTH,
  ],
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/** Set of role names that have entries in ROLE_DEFAULT_PERMISSIONS (computed once). */
const VALID_ROLES = new Set<string>(Object.keys(ROLE_DEFAULT_PERMISSIONS));

/**
 * Resolve reference permissions from a list of role names for administration
 * previews and initial-policy tooling.
 *
 * This is critical for the case where the backend returns `permissions: []` but the user
 * has roles assigned. Each role maps to a known set of default permissions defined in
 * This function must not be used for live authorization. Unknown roles are
 * silently skipped; callers that need diagnostics should use
 * {@link getUnmappedRoles}.
 *
 * @param roles - Array of role name strings (e.g., ['system-admin', 'tenant-admin'])
 * @returns Deduplicated array of permission strings derived from all matched roles
 */
export function resolvePermissionsFromRoles(roles: string[]): string[] {
  const permissionSet = new Set<string>();

  for (const role of roles) {
    if (VALID_ROLES.has(role)) {
      const rolePermissions = ROLE_DEFAULT_PERMISSIONS[role as Role];
      for (const permission of rolePermissions) {
        permissionSet.add(permission);
      }
    }
  }

  return Array.from(permissionSet);
}

/**
 * Identify role names that have no entry in ROLE_DEFAULT_PERMISSIONS.
 * Intended for caller-side diagnostics via LoggerService — not for permission logic.
 *
 * @param roles - Array of role name strings
 * @returns Role names that have no frontend permission mapping
 */
export function getUnmappedRoles(roles: string[]): string[] {
  return roles.filter(role => !VALID_ROLES.has(role));
}

/**
 * Check if a permission string is a valid tenant permission
 */
export function isTenantPermission(permission: string): permission is TenantPermission {
  return Object.values(TENANT_PERMISSIONS).includes(permission as TenantPermission);
}

/**
 * Get all permissions for a resource
 */
export function getPermissionsForResource(resource: string): string[] {
  const permissionSets: Record<string, Record<string, string>> = {
    tenant: TENANT_PERMISSIONS,
    user: USER_PERMISSIONS,
    role: ROLE_PERMISSIONS,
    trading: TRADING_PERMISSIONS,
    strategy: STRATEGY_PERMISSIONS,
    symbol: SYMBOL_PERMISSIONS,
    hft: HFT_PERMISSIONS,
    exchange: EXCHANGE_PERMISSIONS,
    system: SYSTEM_PERMISSIONS,
    schema: SCHEMA_PERMISSIONS,
    dictionary: DICTIONARY_PERMISSIONS,
    migration: MIGRATION_PERMISSIONS,
    backup: BACKUP_PERMISSIONS,
    anomaly: ANOMALY_PERMISSIONS,
    config: CONFIG_PERMISSIONS,
  };

  const set = permissionSets[resource];
  return set ? Object.values(set) : [];
}

/**
 * Reference helper for policy editors. Live UI authorization consumes the
 * explicit backend-derived permission set in AuthStore instead.
 */
export function canPerformTenantOperation(
  userRoles: string[],
  userPermissions: string[],
  requiredPermission: TenantPermission
): boolean {
  // System admins can do anything
  if (userRoles.includes(ROLES.SYSTEM_ADMIN)) {
    return true;
  }

  // Check for specific permission or MANAGE permission
  return userPermissions.includes(requiredPermission) ||
         userPermissions.includes(TENANT_PERMISSIONS.MANAGE);
}
